import { DateTime } from 'luxon';
import { State } from './choices/state';
import { Topic } from './choices/topic';
import { Contributor } from './contributor';
import { DiagnosisVersion } from './diagnosis-version';
import { SymptomLike } from './symptom';

export interface DiagnosisConstructorOpts {
  id?: number;
  state: State;
  name: string;
  synonyms: string[];
  slug?: string;
  topic?: Topic;
  contributors?: Contributor[];
  description?: string;
  asSymptom?: SymptomLike;
  activeVersionId?: number;
  activeVersion?: DiagnosisVersion;
  totalVersions?: number;
  created?: DateTime;
  modified?: DateTime;
}

/**
 * A diagnosis.
 *
 * @author jonmd
 */
export class Diagnosis {
  static createEmpty() {
    return new Diagnosis({
      id: undefined,
      name: '',
      synonyms: [],
      state: State.STUB,
      topic: Topic.fromBlank()
    });
  }

  public readonly id: number | undefined;
  public state: State;

  public name: string;
  public synonyms: string[];
  public slug?: string;
  public topic?: Topic;

  public contributors: Contributor[];

  private readonly _asSymptom?: SymptomLike;

  public readonly activeVersionId?: number;
  public readonly activeVersion?: DiagnosisVersion;

  public readonly totalVersions?: number;
  public readonly created?: DateTime;
  public readonly modified?: DateTime;

  constructor(opts: DiagnosisConstructorOpts) {
    this.id = opts.id;
    this.state = opts.state;
    this.name = opts.name;
    this.synonyms = opts.synonyms;
    this.slug = opts.slug;
    this.topic = opts.topic;
    this.contributors = opts.contributors || [];
    this._asSymptom = opts.asSymptom;
    this.activeVersionId = opts.activeVersionId;
    this.activeVersion = opts.activeVersion;
    this.totalVersions = opts.totalVersions;
    this.created = opts.created;
    this.modified = opts.modified;
  }

  toString() {
    return '<Diagnosis: id=' + this.id + ', name=' + this.name + '>';
  }

  isLinked(): boolean {
    return this._asSymptom !== undefined;
  }

  get synonymsString(): string {
    return this.synonyms.join(', ');
  }

  get asSymptom(): SymptomLike {
    if (this._asSymptom === undefined) {
      throw new Error('Not linked');
    }

    return this._asSymptom;
  }

  get lastModified(): DateTime | undefined {
    return this.modified || this.created;
  }

  toJson() {
    return {
      id: this.id,
      state: this.state.tag,
      name: this.name,
      synonyms: this.synonyms,
      topic: this.topic?.id,
      date_created: this.created?.toISO(),
      date_modified: this.modified?.toISO()
    };
  }

  static fromJson(json: any): Diagnosis {
    const opts: DiagnosisConstructorOpts = {
      id: json.id,
      state: State.fromTag(json.state || ''),
      name: json.name,
      synonyms: json.synonyms || [],
      slug: json.slug,
      topic: Topic.fromId(json.topic),
      contributors: (json.contributors || []).map((c: any) => Contributor.fromJson(c)),
      asSymptom: json.as_symptom ? SymptomLike.fromJson(json.as_symptom) : undefined,
      activeVersionId: json.active_version_id || undefined,
      activeVersion: json.active_version
        ? DiagnosisVersion.fromJson(json.active_version)
        : undefined,
      totalVersions: json.total_versions || undefined,
      created: json.date_created ? DateTime.fromISO(json.date_created) : undefined,
      modified: json.date_modified ? DateTime.fromISO(json.date_modified) : undefined
    };
    return new Diagnosis(opts);
  }
}
